import { CroppingMultipleTargetUpdateOptions } from "@omnia/shared/fields"
import { PlanType, RegionId } from "@omnia/shared/generated/enums"
import { arrayMax, convertGeometryToWkt, CropId, defineComponent, FieldViewModel, getCropName, getDistinctValues, handleApiError, ICrop, ICropping, ICroppingEditContext, IField, IOmniaShell, isDate, IVariety, param } from "../omnia"
import { createNewCropping, updateCropping } from "../strings"
import { IFieldsEditService, IFieldsService } from "../types"
import { getCroppingActivity } from "./activity"
import { getCompositionTotalPct } from "./cropping/composition/composition"
import { CroppingDrawingContext } from "./cropping/drawingContext"
import { CroppingEditContext } from "./cropping/editContext"
import { getCroppingErrorMessages } from "./cropping/errors"
import { createCropManagementEditContext, CropManagementEditContext } from "./cropping/management"
import { editMultipleCroppingsConfirmationDialogConfig } from "./cropping/multiple/dialog"
import { getAvailableTabs, TabKey } from "./cropping/tabs"
import { checkChangeImpactsOnPlans, confirmChangeImpactsOnRequirements, confirmChangeOnNutritionPlans, confirmCropChange } from "./cropping/validate"
import { validateArea } from "./cropping/validateArea"
import { EditCtrl } from "./edit"
import { CreateCroppingCmd } from "./editCmd"
import { omniaResolve } from "@omnia/shared/promises"

/**
 * Stores the last tab open at session level
 * Admittedly a bit lazy, but will be rewritten in React at some point,
 * so does not seem worth doing more than than
 */
let lastTab: TabKey = "crop";

/**
 * List of plan types that depend on cropping information & so should be shown in "Affected Plans" section.
 *
 * @see https://dev.azure.com/HLH-omnia/Omnia/_workitems/edit/2556#22097814
 */
const planTypesThatDependOnCroppingInfo: PlanType[] = [PlanType.NutritionPlan, PlanType.NutritionPlan2024, PlanType.SprayPlan, PlanType.SeedPlan];

export class EditCroppingCtrl extends EditCtrl<ICropping> implements ICroppingEditContext {

    static create() {

        return defineComponent(EditCroppingCtrl, "app/fields/edit/cropping.html", {
            fieldsMap: "="
        })

    }

    readonly errorMessages = getCroppingErrorMessages(this)

    readonly tabs = getAvailableTabs(this.svc.getSession())

    readonly managementConfig = omnia.crops.getCropManagementConfig(this.svc.getSession());

    tab: TabKey = lastTab ?? "crop"

    private _ctxt: CroppingEditContext

    private _crops: ICrop[]
    private _varieties: IVariety[]

    fixedField: boolean
    fields: IField[]

    currentYearCrops: FieldViewModel[]
    cropDescriptions: string[]

    disabled: boolean

    scotland: boolean

    residues = param.CropResidue()
    actualDataCollapsed: boolean = true

    /** Crop used to check crop protection approvals */
    auditCrop: ICrop

    /** SFI context */
    mgmt: CropManagementEditContext


    static $inject = ["$scope", "OmniaFieldsService", "OmniaFieldsEditService", "OmniaMapTopologyService", "OmniaShell"]

    constructor(
        $scope: ng.IScope,
        svc: IFieldsService,
        editSvc: IFieldsEditService,
        protected topo: omnia.map.IMapTopologyService,
        private readonly shell: IOmniaShell
    ) {

        super($scope, svc, editSvc, topo, shell.isReadOnly(p => p.fields))

    }

    get title(): string {
        return this.newCropping ? createNewCropping : updateCropping
    }

    protected setupTarget() {

        const svc = this.svc

        this.setActivity(getCroppingActivity(this._create, this.target))

        this.scotland = (svc.holding.Region == RegionId.Scotland)

        this.fields = this.svc.fields.map(f => f.field)
        this.currentYearCrops = this.svc.cropping.filter(c => c.currentYear)

        this.cropDescriptions = getDistinctValues(this.svc.cropping.map(c => c.cropping.CropDescr).filter(c => !!c), d => d)

        this._multipleTargets = false

        let previousCropping: ICropping = null

        if (this.execArguments instanceof CreateCroppingCmd) {
            const cmd = this.execArguments
            if (cmd.previousCropping) previousCropping = cmd.previousCropping.cropping
        }

        const years = svc.holding.CropYears.map(y => y.YearId)

        if (this.newCropping) {
            const lastYear = arrayMax(years, y => y)
            this.target.YearId = Math.min(lastYear, previousCropping ? (previousCropping.YearId + 1) : svc.yearId)
        }
        else {
            const cropping = this.svc.findCropping(this.target.CroppingId)
            this.disabled = cropping && cropping.disabled
        }

        this._ctxt = new CroppingEditContext(
            svc.holding.Region,
            this.target,
            previousCropping,
            () => this.form,
            () => this.getCurrentArea(),
            years
        )

        this.fixedField = !!this.target.Field

        this.$scope.$watch("vm.startDate", () => this.validateDatesAndArea(true))
        this.$scope.$watch("vm.endDate", () => this.validateDatesAndArea(false))
        this.$scope.$watchGroup(["vm.field", "vm.area", "vm.target.ManureSpreadArea"], () => this.validateDatesAndArea())

        this.$scope.$watchGroup(["vm.crop", "vm.target.SowingDate", "vm.target.Destroyed"], () => this.updateAuditCrop())

        this.actualDataCollapsed = !(!!this.target.ActualYield || !!this.target.ActualResidue)

        this._crops = svc.crops.filter(c => (!c.Deleted) || (c.CropId == this.target.CropId))
        this.setupVarieties()

        this.mgmt = createCropManagementEditContext(svc, this.readOnly, this.target.Management?.Options, () => this.area)

    }

    private setupVarieties() {

        const { crop } = this
        const variId = this.target.VarietyId

        this._varieties = (crop?.Varieties || [])
            .filter(v => (!v.Deleted) || (v.CropVarietyId == variId))

    }

    get ctxt() {
        return this._ctxt
    }

    openTab(tab: TabKey) {
        lastTab = this.tab = tab
    }

    get newCropping() {
        return this._create
    }

    get disableEdits() {
        return this.boundaryChanging
    }

    get hideInputs() {
        return this.boundaryChanging
    }

    get disableEditVariety() {
        return this.boundaryChanging
    }

    get disableEditCrop() {
        if ((!this.newCropping) && this.target) {
            if (this.hasTargetPlansThatDependOnCroppingInfo) return true
        }
    }

    /**
     * Gets unique plans from across all selected targets.
     * @returns A list of unique plans.
     */
    getTargetPlans() {
        return this.getTargets().flatMap(x => x.Plans ?? []).unique(x => x.PlanId).filter(Boolean);
    }

    /**
     * Gets unique plans from across all selected targets that depend on the cropping info.
     * @returns A list of unique plans.
     */
    getTargetPlansThatDependOnCroppingInfo() {
        return this.getTargetPlans().filter((x) => planTypesThatDependOnCroppingInfo.includes(x.Type));
    }

    /**
     * Whether this cropping is in a plan that depends on cropping information
     */
    get hasTargetPlansThatDependOnCroppingInfo() {
        return !!this.getTargetPlansThatDependOnCroppingInfo().length;
    }

    getCropName(cropping: ICropping): string {
        return getCropName(cropping)
    }

    get crops() {
        return this._crops
    }

    get varieties() {
        return this._varieties
    }

    get field(): IField {
        return this._ctxt.field
    }

    set field(value: IField) {
        this._ctxt.field = value
    }

    get previousCroppings() {
        return this._ctxt.previousCroppings
    }

    get previousCropping() {
        return this._ctxt.previousCropping
    }

    set previousCropping(value) {
        this._ctxt.previousCropping = value
    }

    showPreviousSelector() {

        if (!this.crop) return false
        if (this.hideInputs) return false
        if (this.multipleTargets) return false

        const prev = this._ctxt.previousCroppings
        return (prev && (prev.length > 1))

    }

    get cropRequired(): boolean {
        return this._ctxt.cropRequired
    }

    get crop() {
        return this._ctxt.crop
    }

    set crop(value: ICrop) {
        this._ctxt.crop = value
        this.setupVarieties()
    }

    get originalVariety() {
        return this._ctxt.originalVariety
    }

    get uncropped(): boolean {
        return this._ctxt.uncropped
    }

    get composable(): boolean {
        var crop = this.crop
        if (crop) return crop.Composable
    }

    get cropYear() {
        return this._ctxt.cropYear
    }

    set cropYear(value) {
        this._ctxt.cropYear = value
    }

    get cropYears() {
        return this._ctxt.cropYears
    }

    get area(): number {
        return this.getCurrentArea()
    }

    set area(value: number) {
        if (!this.useBoundaryArea()) {
            this.target.Area = value
        }
    }

    getCurrentArea(): number {

        if (this.useBoundaryArea() && this._editBoundaryVm && this._editBoundaryVm.editCount) {
            return Math.round(this._editBoundaryVm.currentArea / 100) / 100
        }

        return this.target.Area

    }

    getBoundaryArea() {

        const a = this._editBoundaryVm?.currentArea || this._editBoundaryVm?.initialArea

        if (!a) {
            return this.target.BoundaryArea
        }

    }

    useBoundaryArea(): boolean {
        return this._ctxt.useBoundaryArea()
    }

    useDestructionDate(): boolean {
        return this._ctxt.useDestructionDate
    }

    useHarvestDate(): boolean {
        return !this.useDestructionDate()
    }

    canSwitchDateType(): boolean {
        return this._ctxt.canSwitchDateType() && (!this.disableEdits)
    }

    setDateType(destruction: boolean) {
        this.target.Destroyed = destruction
    }

    getEndDateName(): string {
        return (this.useDestructionDate() ? "destruction" : "harvest")
    }

    isDestructionDateReqd() {
        const { crop } = this
        if (crop && crop.Perennial) return false
        return this.target.Destroyed || crop.DestructionDateReqd
    }

    get startDate(): Date {
        return this.target.SowingDate
    }

    get endDate(): Date {
        return (this.useDestructionDate() ? this.target.DestructionDate : this.target.HarvestDate)
    }

    set endDate(value: Date) {
        this._ctxt.endDate = value
    }

    get dateErrors() {
        return this._ctxt.dateErrors
    }

    hasDateErrors() {
        return !!this._ctxt.dateErrors
    }

    get boundaryChanged(): boolean {
        return (this._editBoundaryVm && this._editBoundaryVm.hasChanged)
    }

    get canEnableMultipleTargets(): boolean {
        const { boundaryChanged, boundaryChanging, disabled, readOnly } = this
        return !(boundaryChanged || boundaryChanging || disabled || readOnly)
    }

    setMultipleTargets(enable: boolean) {

        this.editSvc.multiple = enable

        if (enable != this._multipleTargets) {

            this._multipleTargets = enable

            if (!enable) {
                this.svc.selectSingleCropping(this.target.CroppingId)
            }

            this._editBoundaryVm.enableMultiSelection(enable)
        }

    }

    syncSelectedTargetsToMap() {
        this.svc.selectItems(this.currentYearCrops.filter(c => c.selected), true)
    }

    getUsableArea() {
        return this._ctxt.getUsableArea()
    }

    canEditCropName() {
        const { crop } = this
        return (crop && crop.CropId == CropId.Other)
    }

    public canEditBoundary(): boolean {
        return (!(this.multipleTargets || this.disabled || this.readOnly))
    }

    getTargets(): ICropping[] {
        return this.editSvc.cropping;
    }

    /**
     * Whether multiple (> 1) targets have been selected.
     */
    get hasMultipleTargets(): boolean {
        return this.getTargets().length > 1;
    }

    getTargetViewModels(): FieldViewModel[] {
        return this.getTargets().map(x => this.svc.findCropping(x.CroppingId)).filter(Boolean);
    }

    validateDatesAndArea(startChanged?: boolean) {

        if (this.disableEdits) return

        this._ctxt.dateValidationService.validateDates(startChanged)
        validateArea(this.target, this)

        this.mgmt.validate()

    }

    updateAuditCrop() {

        this.auditCrop = undefined

        const { crop, target, cropYear, svc } = this
        if (!target) return
        if (!crop) return
        if (!cropYear) return
        if (!(crop.WinterCropId && crop.SpringCropId)) return

        const { SowingDate: date, Destroyed: destroyed } = target

        if (destroyed) return
        if (!isDate(date)) return

        const spring = (cropYear.Year === date.getFullYear()) && (date.getMonth() >= 1)

        const auditCropId = spring
            ? crop.SpringCropId
            : crop.WinterCropId

        if (!auditCropId) return
        if (auditCropId === crop.CropId) return

        this.auditCrop = svc.crops.find(c => c.CropId === auditCropId)

    }

    canCommit(): boolean {
        if (!this.hasTargetPlansThatDependOnCroppingInfo) {
            if (this.crop && this.composable && this.target.Composition && this.target.Composition.length) {
                var total = getCompositionTotalPct(this.target.Composition);
                if (total != 1) return false;
            }
        }

        // We need at least one target.
        if (!this.hasTargets()) return false;

        // If in "Multiple Targets" mode, you must select more than one target.
        if (this.multipleTargets && !this.hasMultipleTargets) return false;

        return super.canCommit();
    }

    async confirmMultipleTargets() {
        return await this.shell.showComponentAsDialog(editMultipleCroppingsConfirmationDialogConfig, {
            targets: this.getTargetViewModels(),
            affectedPlans: this.getTargetPlansThatDependOnCroppingInfo(),
            managementConfig: this.managementConfig,
        });
    }

    async commitEdit() {
        this.validateDatesAndArea();

        if (!this.canCommit()) {
            this._showValidationErrors = true;
            return;
        }

        this._showValidationErrors = false;

        let targets = this.getTargets();
        if (!targets.length) return;

        const single = targets.length == 1;

        if (this.disableEdits && !this.newCropping) {
            return;
        }

        // If multiple, check for confirmation & target properties.
        let multipleTargetOptions: CroppingMultipleTargetUpdateOptions | undefined = undefined;
        if (targets.length > 1) {
            const confirmMultipleTargets = await this.confirmMultipleTargets();

            // If cancelled, abort edit.
            if (!confirmMultipleTargets.confirmed) return;

            // Only update the selected targets/properties.
            targets = targets.filter((x) => confirmMultipleTargets.croppingIds.includes(x.CroppingId));
            multipleTargetOptions = confirmMultipleTargets.multipleTargetOptions;
        }

        const model = this._ctxt.getUpdatedData(single, this.mgmt);

        const confirmation = this.confirmChanges(model, targets);
        if (!confirmation) return;

        confirmation.then((ok) => {
            if (ok) {
                if (single) {
                    if (this._editBoundaryVm) {
                        this._editBoundaryVm.setBoundaryData(model);
                    }

                    if (!model.Boundary && this.target.Boundary) {
                        model.Boundary = convertGeometryToWkt(this.target.Boundary);
                        model.BoundaryArea = this.target.BoundaryArea;
                    }

                    if (model.Boundary && model.BoundaryArea && this.useBoundaryArea()) {
                        model.Area = model.BoundaryArea;
                    }
                }

                let result: ng.IPromise<any>;

                if (this.newCropping) {
                    result = this.svc.createCropping(model);
                } else {
                    const croppingIds = targets.map((t) => t.CroppingId);

                    result = this.svc.updateCropping(croppingIds, model, undefined, multipleTargetOptions);
                }

                result
                    .then((r) => this.cancelEdit())
                    .catch((r) => handleApiError(this.shell, r, { resourceType: "cropping" }));
            }
        });
    }

    createBoundaryEditContext() {

        const prev = () => this._ctxt.getCurrentPrevious()

        return new CroppingDrawingContext(this.target, prev, this.fieldsMap, this.topo, () => this.svc.setView(true), this._drawingSettings)

    }

    private confirmChanges(model: ICropping, targets: ICropping[]): ng.IPromise<boolean> {

        if (this.newCropping) return omniaResolve(true)

        if (!(targets && targets.length)) return null

        targets = targets.map(t => this.svc.findCropping(t.CroppingId)).filter(c => !!c).map(c => c.cropping)

        const target = targets.find(t => t.CroppingId == this.target.CroppingId)

        const confirm =
            confirmCropChange(this.shell, this.crop, targets)
                .then(ok => ok && checkChangeImpactsOnPlans(this.shell, model, target))
                .then(ok => ok && confirmChangeImpactsOnRequirements(this.shell, model, target, targets))
                .then(ok => ok && confirmChangeOnNutritionPlans(this.shell, target))

        return confirm

    }

    public readonly maltingOptions = param.CropMaltingOption()

    public supportsMaltingOptions() {
        return this._ctxt?.supportsMaltingOptions()
    }

    public supportsHighNDistilling() {
        return this._ctxt?.supportsHighNDistilling()
    }


}
